'use client';

import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CountryIso2 } from 'react-international-phone';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/nextjs';
import { CheckCircle as CheckCircleIcon, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';

import type { CartTotals, ID, Price as PriceValue, SellableType } from '@nutralab/api';
import {
    Button,
    Card,
    Flex,
    FormGroup,
    Heading,
    Icon,
    IconButton,
    Input,
    Modal,
    PhoneInput,
    Price,
    QuantitySelector,
    Text,
} from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './CreateProductOrderModal.module.scss';

import { OrderSuccessBanner } from '@/app/_components/OrderSuccessBanner';
import { RegisterDiscountCard } from '@/app/_components/RegisterDiscountCard';
import { useAuth, useProfilePrefill } from '@/app/_providers/AuthProvider';
import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';
import { calculateCart } from '@/shared/api/actions/cart';
import { createOrder } from '@/shared/api/actions/order';
import { ANALYTICS_GOALS, GA_EVENTS } from '@/shared/constants';
import { useDebouncedValue } from '@/shared/hooks';
import { ModalProps } from '@/shared/types';
import {
    calcTotalPrice,
    emailField,
    getCurrencySign,
    phoneField,
    reachGoal,
    sendGaEvent,
    stringMin,
    withAlpha,
} from '@/shared/utils';

const CALCULATE_DEBOUNCE_MS = 300;

const createSchema = (
    t: ReturnType<typeof useTranslation>['t'],
): z.ZodObject<{
    name: z.ZodString;
    phone: z.ZodEffects<z.ZodString, string, string>;
    email: z.ZodOptional<z.ZodUnion<[z.ZodLiteral<''>, z.ZodString]>>;
    delivery_address: z.ZodString;
}> =>
    z.object({
        name: withAlpha(t, stringMin(t, 2)),
        phone: phoneField(t),
        email: z.union([z.literal(''), emailField(t)]).optional(),
        delivery_address: stringMin(t, 5),
    });

type FormValues = z.infer<ReturnType<typeof createSchema>>;

export interface CreateProductOrderModalData {
    productId: ID;
    productType: SellableType;
    name: string;
    imageSrc: string;
    price: string;
    currencySign: string;
}

type CreateProductOrderModalProps = ModalProps<CreateProductOrderModalData>;

interface CalculatedPrice {
    quantity: number;
    totals: CartTotals;
    unitPrice: PriceValue | null;
}

/** Quick order of one product: quantity with its price, then name, phone and address. */
export function CreateProductOrderModal({ onClose, zIndex, data }: CreateProductOrderModalProps): ReactElement {
    const { t, i18n } = useTranslation();
    const { country, region } = useSiteConfig();
    const schema = useMemo(() => createSchema(t), [t]);
    const {
        register,
        control,
        getValues,
        setValue,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        mode: 'onTouched',
    });

    useProfilePrefill({
        getValue: (field) => getValues(field),
        setValue: (field, value) => setValue(field, value),
    });

    const { imageSrc, name, price, currencySign } = data;
    const [quantity, setQuantity] = useState(1);
    const [isSuccess, setIsSuccess] = useState(false);
    const { isAuthenticated } = useAuth();
    const debouncedQuantity = useDebouncedValue(quantity, CALCULATE_DEBOUNCE_MS);
    const [calculated, setCalculated] = useState<CalculatedPrice | null>(null);

    // The provider mounts the modal anew on every open, so this runs once per show.
    useEffect(() => {
        sendGaEvent(region.code, GA_EVENTS.OPEN_FAST_CLAIM);
    }, [region.code]);

    // The backend prices it for everyone (a guest gets no discounts); the local price shows until it answers.
    // isAuthenticated: signing in changes the discounts.
    useEffect(() => {
        let cancelled = false;

        calculateCart([{ type: data.productType, id: data.productId, qty: debouncedQuantity }])
            .then((result) => {
                if (cancelled) {
                    return;
                }
                setCalculated(
                    result.success
                        ? {
                              quantity: debouncedQuantity,
                              totals: result.data.totals,
                              unitPrice: result.data.items[0]?.final_unit_price ?? null,
                          }
                        : null,
                );
            })
            .catch((err) => {
                if (cancelled) {
                    return;
                }
                Sentry.captureException(err);
                setCalculated(null);
            });

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, data.productType, data.productId, debouncedQuantity]);

    const totals = calculated?.totals;
    const isRecalculating = calculated !== null && calculated.quantity !== quantity;
    const hasDiscount = totals ? parseFloat(totals.subtotal.amount) > parseFloat(totals.total.amount) : false;
    const discountAmount = totals ? parseFloat(totals.discount.amount) : 0;

    const onSubmit: SubmitHandler<FormValues> = async (formData) => {
        try {
            await createOrder({
                client_name: formData.name,
                phone: formData.phone,
                delivery_address: formData.delivery_address,
                items: [{ type: data.productType, id: data.productId, qty: quantity }],
            });

            // Sent on the submit, not when the screen renders: a reload or re-render can't repeat it.
            reachGoal(region.code, ANALYTICS_GOALS.FAST_CLAIM_CREATED);
            setIsSuccess(true);
        } catch (err) {
            Sentry.captureException(err);
            toast.error(t('error.general'));
        }
    };

    if (isSuccess) {
        return (
            <Modal open onClose={onClose} zIndex={zIndex} title={name}>
                <Card className={styles.card}>
                    <div className={styles['image-wrapper']}>
                        <img src={imageSrc} alt={name} />
                    </div>

                    <div className={styles['right-column']}>
                        <div className={classNames(styles['modal-header'], 'mb-6')}>
                            <IconButton
                                className={styles.close}
                                variant="outlined"
                                color="soft"
                                aria-label={t('order-modal.close')}
                                size="sm"
                                onClick={onClose}
                            >
                                <XIcon />
                            </IconButton>
                        </div>

                        <div className={styles['success-content']}>
                            <Icon icon={CheckCircleIcon} size="5xl" color="success" />
                            <Heading size="xl" weight="semibold" align="center">
                                {t('order-modal.success.title')}
                            </Heading>
                            <Text align="center" color="muted" lineHeight="relaxed">
                                {t('order-modal.success.message.received')}
                                <br />
                                {t('order-modal.success.message.thanks')}
                            </Text>
                            <OrderSuccessBanner />
                            <Button onClick={onClose}>{t('order-modal.success.close')}</Button>
                        </div>
                    </div>
                </Card>
            </Modal>
        );
    }

    return (
        <Modal open onClose={onClose} zIndex={zIndex} title={name}>
            <Card className={styles.card}>
                <div className={styles['image-wrapper']}>
                    <img src={imageSrc} alt={name} />
                </div>

                <div className={styles['right-column']}>
                    <div className={classNames(styles['modal-header'], styles['is-spaced'], 'mb-6')}>
                        <Heading size="xl" weight="semibold" className={styles['product-title']}>
                            {name}
                        </Heading>

                        <IconButton
                            className={classNames(styles.close, 'md:ml-auto')}
                            variant="outlined"
                            color="soft"
                            aria-label={t('order-modal.close')}
                            size="sm"
                            onClick={onClose}
                        >
                            <XIcon />
                        </IconButton>
                    </div>

                    <form className={styles.form} onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
                        <FormGroup required label={t('order-modal.field.name.label')} errorText={errors.name?.message}>
                            <Input
                                {...register('name')}
                                placeholder={t('order-modal.field.name.placeholder')}
                                error={Boolean(errors.name)}
                            />
                        </FormGroup>

                        <FormGroup
                            required
                            label={t('order-modal.field.phone.label')}
                            errorText={errors.phone?.message}
                        >
                            <Controller
                                name="phone"
                                control={control}
                                render={({ field }) => (
                                    <PhoneInput
                                        value={field.value}
                                        onChange={field.onChange}
                                        defaultCountry={country.code.toLowerCase() as CountryIso2}
                                        locale={i18n.language}
                                        countryButtonLabel={t('form.phone.country-button')}
                                        countryListLabel={t('form.phone.country-list')}
                                        error={Boolean(errors.phone)}
                                    />
                                )}
                            />
                        </FormGroup>

                        <FormGroup
                            required
                            label={t('order-modal.field.delivery_address.label')}
                            errorText={errors.delivery_address?.message}
                        >
                            <Input
                                {...register('delivery_address')}
                                placeholder={t('order-modal.field.delivery_address.placeholder')}
                                error={Boolean(errors.delivery_address)}
                            />
                        </FormGroup>

                        <FormGroup label={t('order-modal.field.email.label')} errorText={errors.email?.message}>
                            <Input
                                {...register('email')}
                                placeholder={t('order-modal.field.email.placeholder')}
                                error={Boolean(errors.email)}
                            />
                        </FormGroup>

                        <div className="mt-auto">
                            <RegisterDiscountCard hasDiscount={totals ? hasDiscount : null} className="mb-4" />

                            <div className={styles.actions}>
                                {totals ? (
                                    <div
                                        className={classNames(
                                            styles.total,
                                            isRecalculating && styles['is-recalculating'],
                                        )}
                                    >
                                        <Price
                                            amount={totals.total.amount}
                                            oldAmount={hasDiscount ? totals.subtotal.amount : null}
                                            currencySign={getCurrencySign(totals.total.currency)}
                                        />

                                        {discountAmount > 0 && (
                                            <Text size="sm" color="muted" className="mt-2">
                                                {t('cart.summary.discount')}: −{totals.discount.amount}{' '}
                                                {getCurrencySign(totals.discount.currency)}
                                            </Text>
                                        )}
                                    </div>
                                ) : (
                                    <Heading weight="semibold" className={styles.nowrap}>
                                        {calcTotalPrice(price, quantity)}{' '}
                                        <Text as="span" size="lg">
                                            {currencySign}
                                        </Text>
                                    </Heading>
                                )}

                                <Flex direction="column" align="center" gap={2}>
                                    <QuantitySelector
                                        value={quantity}
                                        onChange={setQuantity}
                                        labels={{
                                            decrease: t('quantity-selector.decrease'),
                                            increase: t('quantity-selector.increase'),
                                            quantity: t('quantity-selector.quantity'),
                                        }}
                                    />

                                    {calculated?.unitPrice && (
                                        <Text
                                            size="xs"
                                            color="muted"
                                            className={classNames(
                                                styles['per-unit'],
                                                quantity <= 1 && styles['is-hidden'],
                                                isRecalculating && styles['is-recalculating'],
                                            )}
                                            aria-hidden={quantity <= 1 || undefined}
                                        >
                                            {t('cart.item.per-unit', {
                                                price: `${calculated.unitPrice.amount} ${getCurrencySign(calculated.unitPrice.currency)}`,
                                            })}
                                        </Text>
                                    )}
                                </Flex>
                            </div>
                        </div>

                        <Button className="mt-4" type="submit" size="lg" disabled={isSubmitting}>
                            {t('order-modal.submit')}
                        </Button>
                    </form>
                </div>
            </Card>
        </Modal>
    );
}
